import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  CheckCircle2, 
  Package, 
  Truck, 
  Search, 
  Leaf,
  ArrowRight,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const IMAGES = {
  quality: "https://images.pexels.com/photos/8326315/pexels-photo-8326315.jpeg?w=1200&q=80",
  warehouse: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80",
  shipping: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80",
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const QualityPage = () => {
  const qualityProcess = [
    {
      step: '01',
      title: 'Source Verification',
      description: 'We verify all suppliers and farms before onboarding. Each source is evaluated for quality consistency, ethical practices, and reliability.',
      icon: Search
    },
    {
      step: '02',
      title: 'Pre-Shipment Inspection',
      description: 'Every batch undergoes thorough inspection at the warehouse. We check for grade, moisture content, foreign matter, and overall quality.',
      icon: ClipboardCheck
    },
    {
      step: '03',
      title: 'Laboratory Testing',
      description: 'Representative samples are tested in accredited labs for moisture, purity, and other parameters as per buyer specifications.',
      icon: Shield
    },
    {
      step: '04',
      title: 'Export Documentation',
      description: 'Complete documentation including certificates of origin, phytosanitary certificates, and quality reports accompany every shipment.',
      icon: CheckCircle2
    }
  ];

  const standards = [
    {
      icon: Leaf,
      title: 'Sourcing Standards',
      points: [
        'Direct relationships with established farmers',
        'Regular farm visits and quality audits',
        'Seasonal crop monitoring',
        'Traceability from farm to shipment'
      ]
    },
    {
      icon: Package,
      title: 'Packaging Standards',
      points: [
        'Export-grade packaging materials',
        'Moisture-resistant protection',
        'Proper labeling as per destination country',
        'Container optimization for safe transit'
      ]
    },
    {
      icon: Truck,
      title: 'Logistics Standards',
      points: [
        'Temperature-controlled storage where needed',
        'Established shipping line partnerships',
        'Real-time shipment tracking',
        'Insurance coverage for all shipments'
      ]
    }
  ];

  const specifications = [
    { parameter: 'Moisture Content', standard: 'As per product specification (typically 5-8%)' },
    { parameter: 'Foreign Matter', standard: 'Minimal, within acceptable limits' },
    { parameter: 'Grade/Quality', standard: 'As agreed with buyer' },
    { parameter: 'Packaging', standard: 'New, clean, export-grade materials' },
    { parameter: 'Documentation', standard: 'Complete and accurate' },
    { parameter: 'Inspection', standard: 'Pre-shipment inspection available' },
  ];

  return (
    <div data-testid="quality-page">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-primary">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-accent font-medium text-sm tracking-wider uppercase">Quality & Infrastructure</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 mb-6">
              Quality is Our Foundation
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Our commitment to quality ensures every shipment meets international standards. 
              From sourcing to delivery, we maintain strict quality protocols.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quality Commitment Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mb-6">
                Our Quality Commitment
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  At Nirmal Exports, quality isn't just a department—it's embedded in everything we do. 
                  We understand that our international buyers depend on us for consistent, reliable quality, 
                  and we take that responsibility seriously.
                </p>
                <p>
                  Our quality management approach covers the entire supply chain—from selecting the right 
                  farmers and suppliers to ensuring products are properly packaged and shipped. We work 
                  with experienced inspectors and maintain detailed documentation for full traceability.
                </p>
                <p>
                  We're transparent about our capabilities and limitations. If a specific grade or 
                  specification isn't available, we'll tell you upfront rather than compromise on quality.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={IMAGES.quality}
                alt="Quality Control"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quality Process Section */}
      <section className="py-20 lg:py-28 bg-surface">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <span className="text-secondary font-medium text-sm tracking-wider uppercase">Our Process</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mt-3 mb-4">
              Quality Assurance Process
            </h2>
            <p className="text-muted-foreground text-lg">
              A systematic approach to ensure every shipment meets your expectations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityProcess.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden">
                  <CardContent className="p-6">
                    <span className="absolute top-4 right-4 text-6xl font-heading font-bold text-secondary/10">
                      {item.step}
                    </span>
                    <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                      <item.icon className="w-7 h-7 text-secondary" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-primary mb-2">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <span className="text-secondary font-medium text-sm tracking-wider uppercase">Our Standards</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mt-3 mb-4">
              Infrastructure & Standards
            </h2>
            <p className="text-muted-foreground text-lg">
              Comprehensive standards across sourcing, packaging, and logistics
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {standards.map((standard, index) => (
              <motion.div
                key={standard.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-2 border-border hover:border-secondary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <standard.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-xl text-primary mb-4">
                      {standard.title}
                    </h3>
                    <ul className="space-y-3">
                      {standard.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications Table Section */}
      <section className="py-20 lg:py-28 bg-surface">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mb-6">
                General Quality Parameters
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                These are general quality parameters we maintain across our product range. 
                Specific parameters vary by product and can be customized based on buyer requirements.
              </p>
              
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="text-left py-4 px-6 font-heading font-semibold">Parameter</th>
                      <th className="text-left py-4 px-6 font-heading font-semibold">Standard</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specifications.map((spec, index) => (
                      <tr key={spec.parameter} className={index % 2 === 0 ? 'bg-white' : 'bg-surface'}>
                        <td className="py-4 px-6 text-sm font-medium text-primary">{spec.parameter}</td>
                        <td className="py-4 px-6 text-sm text-muted-foreground">{spec.standard}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={IMAGES.warehouse}
                alt="Warehouse Operations"
                className="rounded-2xl shadow-2xl w-full h-[300px] object-cover mb-6"
              />
              <img 
                src={IMAGES.shipping}
                alt="Shipping Container"
                className="rounded-2xl shadow-2xl w-full h-[300px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Questions About Our Quality Process?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              We're happy to discuss our quality standards and provide more details about 
              how we can meet your specific requirements.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-accent text-primary hover:bg-accent/90 font-semibold px-8"
                data-testid="quality-cta-btn"
              >
                Contact Our Quality Team
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default QualityPage;
